import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy
} from '@angular/core';

import {
  ActivatedRoute,
  Router,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError
} from '@angular/router';

import { filter, map, mergeMap } from 'rxjs/operators';

import { AosService } from './core/services/aos.service';
import { SeoService } from './core/services/seo.service';
import { environment } from '../environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent
  implements OnInit, AfterViewInit, OnDestroy {

  baseUrl = environment.assetsUrl;
  title = 'ghrcemp-angular';

  loading = true;
  routeLoading = false;

  // ==========================================
  // PAGE LOADER
  // ==========================================

  // Minimum time loader should remain visible
  private readonly MIN_LOADER_TIME = 1000;

  // Records when the application loader started
  private loaderStartTime = Date.now();

  private modalHideHandler!: EventListener;
  private modalShownHandler!: EventListener;

  private theadMutationObserver?: MutationObserver;
  private theadStuckObservers: IntersectionObserver[] = [];
  private theadScanScheduled = false;

  constructor(
    private aosService: AosService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private seo: SeoService
  ) {}

  ngOnInit(): void {

    // ==========================================
    // START LOADER TIMER
    // ==========================================

    this.loaderStartTime = Date.now();


    // ==========================================
    // Initialize AOS
    // ==========================================

    this.aosService.init();


    // ==========================================
    // Route Loader
    // ==========================================

    this.router.events.subscribe(event => {

      if (event instanceof NavigationStart) {
        this.routeLoading = true;
      }

      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.routeLoading = false;
      }

    });


    // ==========================================
    // SEO Meta
    // ==========================================

    this.router.events.pipe(

      filter(event => event instanceof NavigationEnd),

      map(() => {

        let route = this.activatedRoute;

        while (route.firstChild) {
          route = route.firstChild;
        }

        return route;

      }),

      filter(route => route.outlet === 'primary'),

      mergeMap(route => route.data)

    ).subscribe(data => {

      this.seo.update(
        data['title'],
        data['description'],
        data['keywords'],
        data['robots']
      );

    });


    // ==========================================
    // Canonical URL + Close Bootstrap UI
    // ==========================================

    this.router.events.pipe(

      filter(
        (event): event is NavigationEnd =>
          event instanceof NavigationEnd
      )

    ).subscribe(event => {

      const cleanPath = event.urlAfterRedirects
        .split('?')[0]
        .split('#')[0];

      this.seo.updateCanonicalUrl(
        environment.siteUrl + cleanPath
      );


      // ------------------------------------------
      // Close Bootstrap Dropdowns
      // ------------------------------------------

      document.querySelectorAll('.dropdown-menu.show')
        .forEach(menu => {
          menu.classList.remove('show');
        });

      document.querySelectorAll('.dropdown-toggle.show')
        .forEach(toggle => {

          toggle.classList.remove('show');

          toggle.setAttribute(
            'aria-expanded',
            'false'
          );

        });


      // ------------------------------------------
      // Close Mobile Navbar
      // ------------------------------------------

      const navbar =
        document.getElementById('navbarSupportedContent');

      if (navbar) {
        navbar.classList.remove('show');
      }


      // ------------------------------------------
      // Close Bootstrap Modal
      // ------------------------------------------

      document.querySelectorAll('.modal.show')
        .forEach(modal => {

          const modalElement = modal as HTMLElement;

          modalElement.classList.remove('show');

          modalElement.setAttribute(
            'aria-hidden',
            'true'
          );

          modalElement.removeAttribute(
            'aria-modal'
          );

          modalElement.style.display = 'none';

        });


      document.body.classList.remove('modal-open');

      document.body.style.removeProperty(
        'padding-right'
      );


      document.querySelectorAll('.modal-backdrop')
        .forEach(backdrop => {
          backdrop.remove();
        });

    });


    // ==========================================
    // Accessibility Fix for Bootstrap Modal
    // ==========================================

    this.modalShownHandler = (event: Event) => {

      const modal = event.target as HTMLElement;

      if (!modal) {
        return;
      }

      const focusable =
        modal.querySelector<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

      focusable?.focus();

    };


    this.modalHideHandler = (event: Event) => {

      const modal = event.target as HTMLElement;

      const active =
        document.activeElement as HTMLElement | null;

      if (
        modal &&
        active &&
        modal.contains(active)
      ) {
        active.blur();
      }

    };


    document.addEventListener(
      'shown.bs.modal',
      this.modalShownHandler
    );

    document.addEventListener(
      'hide.bs.modal',
      this.modalHideHandler
    );

  }


  // ==========================================
  // AFTER VIEW INIT
  // ==========================================

ngAfterViewInit(): void {

  this.initStickyTheadDetection();

  const elapsed = Date.now() - this.loaderStartTime;

  const remaining = Math.max(
    this.MIN_LOADER_TIME - elapsed,
    0
  );

  setTimeout(() => {
    this.loading = false;
  }, remaining);
}

  // ==========================================
  // STICKY TABLE THEAD DETECTION
  // ==========================================

  private initStickyTheadDetection(): void {

    const STICKY_TOP = 75;

    const observeThead = (thead: HTMLElement) => {

      if (thead.dataset['stuckObserved']) {
        return;
      }

      thead.dataset['stuckObserved'] = 'true';


      const table =
        thead.closest('table');

      if (
        !table ||
        !table.parentElement
      ) {
        return;
      }


      const sentinel =
        document.createElement('div');

      sentinel.style.cssText =
        'height:1px;pointer-events:none;';


      table.parentElement.insertBefore(
        sentinel,
        table
      );


      const observer =
        new IntersectionObserver(

          ([entry]) => {

            thead.classList.toggle(
              'is-stuck',
              !entry.isIntersecting
            );

          },

          {
            rootMargin:
              `-${STICKY_TOP + 1}px 0px 0px 0px`,
            threshold: 0
          }

        );


      observer.observe(sentinel);

      this.theadStuckObservers.push(
        observer
      );

    };


    document
      .querySelectorAll<HTMLElement>('table thead')
      .forEach(observeThead);


    this.theadMutationObserver =
      new MutationObserver(mutations => {

        const hasTableMutation = mutations.some(mutation =>
          Array.from(mutation.addedNodes).some(node =>
            node instanceof HTMLElement &&
            (node.tagName === 'TABLE' || !!node.querySelector('table'))
          )
        );

        if (!hasTableMutation || this.theadScanScheduled) {
          return;
        }

        this.theadScanScheduled = true;

        requestAnimationFrame(() => {

          this.theadScanScheduled = false;

          document
            .querySelectorAll<HTMLElement>('table thead')
            .forEach(observeThead);

        });

      });


    this.theadMutationObserver.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );

  }


  // ==========================================
  // DESTROY
  // ==========================================

  ngOnDestroy(): void {

    document.removeEventListener(
      'shown.bs.modal',
      this.modalShownHandler
    );

    document.removeEventListener(
      'hide.bs.modal',
      this.modalHideHandler
    );


    this.theadMutationObserver?.disconnect();


    this.theadStuckObservers.forEach(
      observer => observer.disconnect()
    );

  }

}
